import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { UserOutlined, EnvironmentOutlined, CreditCardOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ROUTES, GRADE_OPTIONS, SELECTABLE_GRADE_OPTIONS } from '@utils/constants';
import { updateStudent, searchCampuses } from '../../api/students';
import { searchShifts } from '../../api/shifts';
import { getCampusesByIds, ShiftDetails } from '../../api/user';
import { AppHeader } from '@components/layout/AppHeader';
import { AddressSelect } from '../../components/ui/AddressSelect';
import { InfoCard } from '../../components/ui/InfoCard/InfoCard';
import { ModalAlert, type ModalAlertType } from '../../components/ui/Alert/ModalAlert';
import { useToastContext } from '../../context/ToastContext';
import { useCampusFilter } from '../../context/CampusContext';
import { buildCampusSearchQuery } from '../../utils/campusFilter';
import { saveStudentSession } from '@utils/sessionStorage';
import { calculateDistanceWithGoogleJS, type DistanceResult } from '@utils/distance';
import { filterShiftsByGrade } from '@utils/shiftFilter';
import './styles/editdetails.css';
import '@styles/loader.css';

interface EditDetailsState {
  studentId: string;
  student: any;
  isEdit?: boolean;
  fromDashboard?: boolean;
}

interface CampusOption {
  id: string;
  name: string;
  location?: { lat: number; lng: number };
}

interface LocationCoords {
  lat: number;
  lng: number;
}

// Convert dropdown value (CLASS_3) to API format (CLASS_3)
const valueToApiGrade = (value: string): string => {
  if (!value) return '';
  return value;
};

// Get gender icon with color
const getGenderIcon = (gender: string) => {
  switch (gender) {
    case 'boy':
      return <span style={{ fontSize: '20px', color: '#2196F3' }}>♂</span>;
    case 'girl':
      return <span style={{ fontSize: '20px', color: '#E91E63' }}>♀</span>;
    default:
      return <span style={{ fontSize: '20px', color: '#9C27B0' }}>◯</span>;
  }
};

// Check if student needs to complete payment
const isPaymentNeeded = (status: string): boolean => {
  const paidStatuses = ['ACTIVE', 'PENDING_ROUTING', 'PAYMENT_IN_PROGRESS'];
  return !paidStatuses.includes(status);
};

export const EditDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as EditDetailsState;
  const { addToast } = useToastContext();
  const { campusIds, campusSlug } = useCampusFilter();

  const [saving, setSaving] = useState(false);
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [shifts, setShifts] = useState<ShiftDetails[]>([]);
  const [campusesLoading, setCampusesLoading] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  // Distance calculation
  const [distanceInfo, setDistanceInfo] = useState<DistanceResult | null>(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [modalAlertConfig, setModalAlertConfig] = useState<{
    type: ModalAlertType;
    title: string;
    description?: string;
    visible: boolean;
  }>({
    type: 'info',
    title: '',
    description: '',
    visible: false,
  });

  // Map states
  const [selectedLocation, setSelectedLocation] = useState<LocationCoords | null>(() => {
    const loc = state?.student?.location;
    if (loc) {
      // Handle both formats: {lat, lng} and {latitude, longitude}
      const lat = loc.lat ?? loc.latitude;
      const lng = loc.lng ?? loc.longitude;
      if (typeof lat === 'number' && typeof lng === 'number' && isFinite(lat) && isFinite(lng)) {
        return { lat, lng };
      }
    }
    return null;
  });

  // Get student name helper
  const getStudentName = (student: any) => {
    if (student?.name) return student.name;
    if (student?.givenName || student?.familyName) {
      return `${student.givenName || ''} ${student.familyName || ''}`.trim();
    }
    return '';
  };

  // Form data for editing
  const [formData, setFormData] = useState({
    givenName: state?.student?.givenName || getStudentName(state?.student)?.split(' ')[0] || '',
    familyName: state?.student?.familyName || getStudentName(state?.student)?.split(' ').slice(1).join(' ') || '',
    gender: (state?.student?.gender || '').toLowerCase().trim(),
    // Resolved against the full GRADE_OPTIONS (not the restricted selectable list) so a student
    // already recorded under a removed grade (Class 1-6, NEET/JEE Dropper) still displays their
    // actual stored value instead of being silently reset. Only genuinely missing grade data
    // falls back to a currently-selectable default.
    grade: GRADE_OPTIONS.find(g => g.value === state?.student?.grade) || SELECTABLE_GRADE_OPTIONS[0],
    campusId: state?.student?.campusId || '',
    shiftId: state?.student?.shifts?.[0] || '',
    geoAddress: state?.student?.geoAddress || '',
  });

  useEffect(() => {
    if (!state?.studentId || !state?.student) {
      navigate(ROUTES.WELCOME);
      return;
    }
    
    fetchCampuses();
    if (formData.campusId) {
      fetchShifts(formData.campusId);
    }
    
    // Calculate distance on mount
    if (selectedLocation && state?.student?.campusId) {
      calculateStudentDistance();
    }
  }, []);

  useEffect(() => {
    if (formData.campusId) {
      fetchShifts(formData.campusId);
    }
  }, [formData.campusId]);

  // Filter shifts based on grade and handle auto-selection
  const filteredShifts = useMemo(() => {
    return filterShiftsByGrade(shifts, formData.grade?.value || '');
  }, [shifts, formData.grade]);

  // Handle auto-selection and clearing when grade or shifts change
  useEffect(() => {
    // If no grade selected or no shifts available, don't do anything
    if (!formData.grade?.value || shifts.length === 0) return;

    const filtered = filterShiftsByGrade(shifts, formData.grade.value);

    // If only 1 shift after filtering, auto-select it
    if (filtered.length === 1) {
      setFormData(prev => ({ ...prev, shiftId: filtered[0].id }));
    } else if (filtered.length > 1) {
      // If multiple shifts after filtering, check if current selection is still valid
      const isCurrentShiftValid = filtered.some(s => s.id === formData.shiftId);
      if (!isCurrentShiftValid) {
        // Clear shift if currently selected shift is no longer in filtered list
        setFormData(prev => ({ ...prev, shiftId: '' }));
      }
    } else {
      // No shifts match the grade - clear selection
      setFormData(prev => ({ ...prev, shiftId: '' }));
    }
  }, [formData.grade, shifts]);

  // Calculate distance when location changes
  useEffect(() => {
    if (selectedLocation && state?.student?.campusId) {
      calculateStudentDistance();
    }
  }, [selectedLocation, state?.student?.campusId]);

  const fetchCampuses = async () => {
    try {
      setCampusesLoading(true);
      const response = await searchCampuses(buildCampusSearchQuery(campusIds));
      if (response.campuses) {
        const allValid = response.campuses
          .filter(c => c.id && c.name)
          .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));

        let finalCampuses = allValid;

        // If a campus slug is active (e.g. from /allen), filter by name match
        if (campusSlug && !campusIds) {
          const currentCampusId = formData.campusId;
          const slugFiltered = allValid.filter(c =>
            (c.name || '').toLowerCase().includes(campusSlug.toLowerCase()) ||
            c.id === currentCampusId
          );
          // Fallback: if slug matched nothing, show all campuses
          if (slugFiltered.length > 0) {
            finalCampuses = slugFiltered;
          }
        }

        // Fallback: if zid filter returned no results, fetch all campuses
        if (finalCampuses.length === 0 && campusIds && campusIds.length > 0) {
          const fallbackResponse = await searchCampuses('*');
          if (fallbackResponse.campuses) {
            finalCampuses = fallbackResponse.campuses
              .filter(c => c.id && c.name)
              .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
          }
        }

        setCampuses(finalCampuses as CampusOption[]);
      }
    } catch (error) {
      console.error('Error fetching campuses:', error);
      addToast({
        type: 'error',
        title: 'Loading Campuses Failed',
        message: 'Unable to load campuses. Please try again.',
      });
    } finally {
      setCampusesLoading(false);
    }
  };

  const fetchShifts = async (campusId: string) => {
    try {
      setShiftsLoading(true);
      const response = await searchShifts(campusId);
      if (response.shifts) {
        setShifts(response.shifts);
        
        // If grade is already selected, filter shifts and auto-select
        const gradeValue = formData.grade?.value;
        if (gradeValue) {
          const filtered = filterShiftsByGrade(response.shifts, gradeValue);
          if (filtered.length === 1) {
            setFormData(prev => ({ ...prev, shiftId: filtered[0].id }));
          } else if (filtered.length > 1) {
            // Check if current shift is still valid
            const isCurrentShiftValid = filtered.some(s => s.id === formData.shiftId);
            if (!isCurrentShiftValid) {
              setFormData(prev => ({ ...prev, shiftId: '' }));
            }
          } else {
            setFormData(prev => ({ ...prev, shiftId: '' }));
          }
        } else {
          // No grade selected - use original logic
          if (response.shifts.length === 1) {
            setFormData(prev => ({ ...prev, shiftId: response.shifts![0].id }));
          } else if (response.shifts.length > 1) {
            const existingShiftId = formData.shiftId;
            const shiftExists = response.shifts.some(s => s.id === existingShiftId);
            if (!shiftExists) {
              setFormData(prev => ({ ...prev, shiftId: '' }));
            }
          }
        }
      } else {
        setShifts([]);
        setFormData(prev => ({ ...prev, shiftId: '' }));
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      addToast({
        type: 'error',
        title: 'Loading Shifts Failed',
        message: 'Unable to load shifts for this campus. Please try again.',
      });
    } finally {
      setShiftsLoading(false);
    }
  };

  const handleBack = () => {
      navigate(ROUTES.WELCOME);
  };

  const calculateStudentDistance = async () => {
    try {
      setCalculatingDistance(true);

      if (!selectedLocation || !state?.student?.campusId) {
        console.warn('Missing location data for distance calculation', {
          selectedLocation,
          campusId: state?.student?.campusId,
        });
        addToast({
          type: 'error',
          title: 'Distance Calculation Failed',
          message: 'Location or campus information is missing. Please try again.',
        });
        setCalculatingDistance(false);
        return;
      }

      // Fetch campus to get its location
      const campusResponse = await getCampusesByIds([state.student.campusId]);

      if (!campusResponse.campuses?.length || !campusResponse.campuses[0].location) {
        console.warn('Campus location not available', { campuses: campusResponse.campuses });
        addToast({
          type: 'error',
          title: 'Campus Location Unavailable',
          message: 'Unable to retrieve campus location. Please try again.',
        });
        setCalculatingDistance(false);
        return;
      }

      const campusLocation = campusResponse.campuses[0].location;

      if (!campusLocation?.lat || !campusLocation?.lng) {
        console.warn('Campus location coordinates not available', { campusLocation });
        addToast({
          type: 'error',
          title: 'Invalid Campus Location',
          message: 'Campus location coordinates are invalid. Please try again.',
        });
        setCalculatingDistance(false);
        return;
      }

      // Calculate distance using Google Maps DistanceMatrixService
      const result = await calculateDistanceWithGoogleJS(
        selectedLocation,
        { lat: campusLocation.lat || 0, lng: campusLocation.lng || 0 }
      );

      setDistanceInfo(result);
    } catch (err: any) {
      console.error('Error calculating distance:', err);
      addToast({
        type: 'error',
        title: 'Distance Calculation Failed',
        message: 'Unable to calculate distance. Please try again or select a different location.',
      });
    } finally {
      setCalculatingDistance(false);
    }
  };

  const handleCompletePayment = async () => {
    // Check if distance is within serviceable range (45 km)
    if (distanceInfo && distanceInfo.distanceKm > 45) {
      setModalAlertConfig({
        type: 'error',
        title: 'Distance not serviceable',
        description: 'Currently we are unable to serve beyond 45km please try again later.',
        visible: true,
      });
      return;
    }

    try {
      setSaving(true);
      
      // Build update payload with only valid UpdateStudentInput fields
      const updateData: any = {
        primaryParentId: state.student?.primaryParentId,
        status: state.student?.status,
        givenName: formData.givenName,
        familyName: formData.familyName,
        gender: formData.gender,
        campusId: formData.campusId,
        geoAddress: formData.geoAddress,
        shifts: [formData.shiftId],
        grade: valueToApiGrade(formData.grade.value),
        secondaryParentId: state.student?.secondaryParentId || '',
        startDate: state.student?.startDate,
        subscription: state.student?.subscription,
      };
      
      // Location uses lat/lng
      if (selectedLocation && typeof selectedLocation.lat === 'number' && typeof selectedLocation.lng === 'number') {
        updateData.location = {
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
        };
      }
      
      // Update student
      await updateStudent(state.studentId, updateData);
      
      // Get campus and shift details for session storage
      const selectedCampus = campuses.find(c => c.id === formData.campusId);
      const selectedShift = shifts.find(s => s.id === formData.shiftId);
      
      // Save session data compatible with onboarding flow
      saveStudentSession({
        studentId: state.studentId,
        formData: {
          givenName: formData.givenName,
          familyName: formData.familyName,
          gender: formData.gender,
          grade: formData.grade,
          campus: {
            id: selectedCampus?.id || formData.campusId,
            name: selectedCampus?.name || '',
            location: selectedCampus?.location || null,
          },
          shift: selectedShift || { id: formData.shiftId, name: '' },
          geoAddress: formData.geoAddress,
        },
        pickupLocation: selectedLocation ? {
          coordinates: selectedLocation,
          address: formData.geoAddress,
        } : undefined,
        isNewStudent: false,
        ...(state?.student?.tags?.transportType && {transportType: state?.student?.tags?.transportType})
      });
      
      // Show success toast
      addToast({
        type: 'success',
        title: 'Success',
        message: 'Student details updated successfully.',
      });
      
      // Navigate to review details page
      navigate(ROUTES.REVIEW_DETAILS, {
        state: {
          studentId: state.studentId,
          student: {
            ...state.student,
            ...updateData,
          },
          formData: {
            givenName: formData.givenName,
            familyName: formData.familyName,
            gender: formData.gender,
            grade: formData.grade,
            campus: selectedCampus,
            shift: selectedShift,
          },
          campuses,
          shifts,
          pickupLocation: selectedLocation ? {
            coordinates: selectedLocation,
            address: formData.geoAddress,
          } : undefined,
          distance: distanceInfo?.distanceKm,
          duration: distanceInfo?.durationMinutes,
        },
      });
    } catch (err: any) {
      let errorMessage = 'Failed to update student details. Please try again.';
      
      if (err.message?.includes('network') || err.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message?.includes('validation') || err.message?.includes('Validation')) {
        errorMessage = 'Please check all fields are filled correctly.';
      } else if (err.message?.includes('unauthorized') || err.message?.includes('Unauthorized')) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDetails = async () => {
    try {
      setSaving(true);
      
      // Build update payload with only valid UpdateStudentInput fields
      const updateData: any = {
        primaryParentId: state.student?.primaryParentId,
        status: state.student?.status,
        givenName: formData.givenName,
        familyName: formData.familyName,
        gender: formData.gender,
        campusId: formData.campusId,
        geoAddress: formData.geoAddress,
        shifts: [formData.shiftId],
        grade: valueToApiGrade(formData.grade.value),
        secondaryParentId: state.student?.secondaryParentId || '',
        startDate: state.student?.startDate,
        subscription: state.student?.subscription,
      };
      
      // Location uses lat/lng
      if (selectedLocation && typeof selectedLocation.lat === 'number' && typeof selectedLocation.lng === 'number') {
        updateData.location = {
          lat: selectedLocation.lat,
          lng: selectedLocation.lng,
        };
      }
      
      // Update student
      await updateStudent(state.studentId, updateData);
      
      // Show success toast
      addToast({
        type: 'success',
        title: 'Success',
        message: 'Student details saved successfully.',
      });
      
      // Navigate back to dashboard
      navigate(ROUTES.WELCOME);
    } catch (err: any) {
      let errorMessage = 'Failed to save student details. Please try again.';
      
      if (err.message?.includes('network') || err.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message?.includes('validation') || err.message?.includes('Validation')) {
        errorMessage = 'Please check all fields are filled correctly.';
      } else if (err.message?.includes('unauthorized') || err.message?.includes('Unauthorized')) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      addToast({
        type: 'error',
        title: 'Save Failed',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };


  const handleInputChange = (field: string, value: string) => {
    // Apply input restrictions based on field type
    let processedValue = value;
    
    if (field === 'givenName' || field === 'familyName') {
      // Only allow alphabetic characters and spaces
      processedValue = value.replace(/[^a-zA-Z\s]/g, '');
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '40px',
      height: '40px',
      borderColor: '#e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      fontSize: '13px',
      cursor: 'pointer',
      padding: '0 8px',
      '&:hover': { borderColor: '#f5a623' },
    }),
    option: (base: any, optionState: any) => ({
      ...base,
      backgroundColor: optionState.isSelected ? '#f5a623' : optionState.isFocused ? '#fffbeb' : '#ffffff',
      color: '#111827',
      cursor: 'pointer',
      padding: '8px 12px',
      fontSize: '12px',
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      maxHeight: '200px',
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '200px',
      paddingTop: '4px',
      paddingBottom: '4px',
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    input: (base: any) => ({
      ...base,
      margin: '0',
      padding: '0',
    }),
  };

  if (!state?.student) return null;

  return (
    <div className="edit-details-page">
      <AppHeader />

      <div className="edit-details-page__container">
        <div className="edit-details-page__content">
          <div className="edit-details-page__title-section">
            <h1 className="edit-details-page__title">Edit student details</h1>
            <p className="edit-details-page__subtitle">You can update these details before payment confirmation</p>
          </div>

          {/* Student Information Section - Single Card */}
          <div className="edit-details-page__card edit-details-page__student-info-card">
            <div className="edit-details-page__card-header">
              <UserOutlined className="edit-details-page__card-header-icon" />
              <span className="edit-details-page__card-header-title">Student Details</span>
            </div>

            <div className="edit-details-page__form">
              {/* Student Name */}
              <div className="edit-details-page__form-field">
                <label className="edit-details-page__field-label">Student Name</label>
                <div className="edit-details-page__name-row">
                  <input
                    type="text"
                    value={formData.givenName}
                    onChange={(e) => handleInputChange('givenName', e.target.value)}
                    className="edit-details-page__input"
                    placeholder="First name"
                  />
                  <input
                    type="text"
                    value={formData.familyName}
                    onChange={(e) => handleInputChange('familyName', e.target.value)}
                    className="edit-details-page__input"
                    placeholder="Last name"
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="edit-details-page__form-field">
                <label className="edit-details-page__field-label">Gender</label>
                <div className="edit-details-page__gender-group">
                  <button
                    type="button"
                    className={`edit-details-page__gender-btn ${formData.gender === 'boy' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setFormData(prev => ({ ...prev, gender: 'boy' }));
                    }}
                  >
                    <span className="edit-details-page__gender-icon">{getGenderIcon('boy')}</span>
                    <span className="edit-details-page__gender-label">Boy</span>
                  </button>
                  <button
                    type="button"
                    className={`edit-details-page__gender-btn ${formData.gender === 'girl' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setFormData(prev => ({ ...prev, gender: 'girl' }));
                    }}
                  >
                    <span className="edit-details-page__gender-icon">{getGenderIcon('girl')}</span>
                    <span className="edit-details-page__gender-label">Girl</span>
                  </button>
                  <button
                    type="button"
                    className={`edit-details-page__gender-btn ${formData.gender === 'other' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setFormData(prev => ({ ...prev, gender: 'other' }));
                    }}
                  >
                    <span className="edit-details-page__gender-icon">{getGenderIcon('other')}</span>
                    <span className="edit-details-page__gender-label">Other</span>
                  </button>
                </div>
              </div>

              {/* Grade */}
              <div className="edit-details-page__form-field">
                <label className="edit-details-page__field-label">Grade</label>
                <Select
                  options={SELECTABLE_GRADE_OPTIONS}
                  value={formData.grade}
                  onChange={(option) => setFormData(prev => ({ ...prev, grade: option || SELECTABLE_GRADE_OPTIONS[0] }))}
                  placeholder="Select grade"
                  isSearchable={false}
                  styles={selectStyles}
                  classNamePrefix="edit-select"
                />
              </div>

              {/* Campus */}
              <div className="edit-details-page__form-field">
                <label className="edit-details-page__field-label">Campus</label>
                <Select
                  options={campuses.map(c => ({ value: c.id, label: c.name }))}
                  value={campuses.find(c => c.id === formData.campusId) ? { value: formData.campusId, label: campuses.find(c => c.id === formData.campusId)?.name } : null}
                  onChange={(option) => setFormData(prev => ({ ...prev, campusId: option?.value || '' }))}
                  placeholder="Select campus"
                  isLoading={campusesLoading}
                  styles={selectStyles}
                  classNamePrefix="edit-select"
                />
              </div>

              {/* Shift */}
              <div className="edit-details-page__form-field">
                <label className="edit-details-page__field-label">Shift</label>
                <Select
                  options={filteredShifts.map(s => ({ value: s.id, label: s.displayLabel || s.name }))}
                  value={
                    filteredShifts.find(s => s.id === formData.shiftId)
                      ? { value: formData.shiftId, label: filteredShifts.find(s => s.id === formData.shiftId)?.displayLabel || filteredShifts.find(s => s.id === formData.shiftId)?.name }
                      : null
                  }
                  onChange={(option) => setFormData(prev => ({ ...prev, shiftId: option?.value || '' }))}
                  placeholder="Select shift"
                  isLoading={shiftsLoading}
                  isDisabled={!formData.campusId || filteredShifts.length === 1}
                  isSearchable={false}
                  styles={selectStyles}
                  classNamePrefix="edit-select"
                />
              </div>
            </div>
          </div>

          {/* Pickup Location Section - Single Card */}
          <div className="edit-details-page__card edit-details-page__pickup-location-card">
            <div className="edit-details-page__card-header">
              <EnvironmentOutlined className="edit-details-page__card-header-icon" />
              <span className="edit-details-page__card-header-title">Pickup Location</span>
            </div>

            <div className="edit-details-page__form-field">
              <label className="edit-details-page__field-label">Home pickup location</label>
              <AddressSelect
                value={formData.geoAddress || ''}
                onAddressChange={(address) => setFormData(prev => ({ ...prev, geoAddress: address }))}
                onLocationSelect={(location) => {
                  setSelectedLocation(location);
                }}
                initialLocation={selectedLocation || undefined}
                placeholder="Search for your address..."
              />
            </div>

            {calculatingDistance && (
              <div className="edit-details-page__distance-loading">
                <span className="btn-loader btn-loader--small"></span>
                <span>Calculating distance...</span>
              </div>
            )}

            {distanceInfo && !calculatingDistance && (
              <div className="edit-details-page__distance-info">
                <p><strong>Distance:</strong> {distanceInfo.distanceText}</p>
                <p><strong>Travel time:</strong> {distanceInfo.durationText}</p>
              </div>
            )}
          </div>

          {/* Good to Know Card */}
          <InfoCard
            icon={<InfoCircleOutlined style={{ fontSize: '24px', color: '#3B82F6' }} />}
            title="Good to know"
            description="Changes made here will be reflected in your final booking. You can still edit after saving until payment is confirmed."
            className="edit-details-info-card"
            cardStyle={{ marginTop: '24px', marginBottom: '24px' }}
          />

          <div className="edit-details-page__footer">
            <button className="edit-details-page__secondary-btn" onClick={handleBack}>Cancel</button>
            {isPaymentNeeded(state.student?.status) ? (
              <button className={`edit-details-page__primary-btn ${saving ? 'btn-loading' : ''}`} onClick={handleCompletePayment} disabled={saving}>
                {saving ? (<><span className="btn-loader btn-loader--light"></span>Processing...</>) : (<><CreditCardOutlined /> Complete Payment</>)}
              </button>
            ) : (
              <button className={`edit-details-page__primary-btn ${saving ? 'btn-loading' : ''}`} onClick={handleSaveDetails} disabled={saving}>
                {saving ? (<><span className="btn-loader btn-loader--light"></span>Saving...</>) : ('Save Details')}
              </button>
            )}
          </div>

          {/* Distance Alert Modal */}
          <ModalAlert
            type={modalAlertConfig.type}
            title={modalAlertConfig.title}
            description={modalAlertConfig.description}
            visible={modalAlertConfig.visible}
            onConfirm={() => setModalAlertConfig({ ...modalAlertConfig, visible: false })}
            confirmText="OK"
          />

          {/* Loading Modal Overlay */}
          {saving && (
            <div className="edit-details-page__modal-overlay">
              <div className="edit-details-page__modal">
                <span className="btn-loader btn-loader--dark" style={{ width: 40, height: 40 }}></span>
                <h3>{isPaymentNeeded(state.student?.status) ? 'Processing Payment' : 'Saving Details'}</h3>
                <p>Please wait while we {isPaymentNeeded(state.student?.status) ? 'process your payment' : 'save your details'}...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
